import React, { Suspense } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const Agenda = React.lazy(() => import('../Agenda'));

interface TeleconsultAgendaDrawerProps {
  open: boolean;
  onClose: () => void;
  patientId?: string;
  patientName?: string;
}

/**
 * Agenda em painel lateral — a chamada Jitsi continua no fundo (não desmonta a página).
 */
const TeleconsultAgendaDrawer: React.FC<TeleconsultAgendaDrawerProps> = ({
  open,
  onClose,
  patientId,
  patientName,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 'min(920px, 94vw)' },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <CalendarMonthIcon sx={{ color: '#0f766e' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Agenda
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {patientName
              ? `Agende retorno ou nova consulta para ${patientName} — a videochamada continua ativa.`
              : 'Agende consultas sem sair da teleconsulta.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fechar agenda" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: '#f8fafc' }}>
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
              <CircularProgress sx={{ color: '#14B8A6' }} />
            </Box>
          }
        >
          {open && (
            <Agenda
              embeddedInTeleconsult
              expandedLayout
              defaultPatientId={patientId}
              defaultPatientName={patientName}
            />
          )}
        </Suspense>
      </Box>

      <Alert severity="info" sx={{ mx: 2, mb: 1.5, borderRadius: 2, flexShrink: 0 }}>
        Feche este painel para voltar ao vídeo e à evolução. A chamada não é encerrada.
      </Alert>
    </Drawer>
  );
};

export default TeleconsultAgendaDrawer;
